import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import type { ColetaAoVivo, ColetaEstado, ColetaResumo } from '../../api/types';

/**
 * A coleta do personagem (techspec-coleta §07): o gump ao vivo vem do evento `co` da sala; o resto
 * (chances, cofre, ferramenta, resumo) vem de `GET /api/coleta`, relido a cada 10 s enquanto coleta.
 * No mock não há sala: o estado ao vivo sai da ordem que o mock calcula pelo relógio.
 */
export function useColeta(hub: ColetaAoVivo | null, net: boolean, onResumo?: (r: ColetaResumo) => void) {
  const [estado, setEstado] = useState<ColetaEstado | null>(null);
  const [mockVivo, setMockVivo] = useState<ColetaAoVivo | null>(null);
  const resumoRef = useRef(onResumo);
  resumoRef.current = onResumo;

  const recarregar = useCallback(async () => {
    try {
      const e = await api.getColeta();
      setEstado(e);
      if (e.resumo) resumoRef.current?.(e.resumo);
      if (!net) setMockVivo(vivoDoMock(e));
      return e;
    } catch {
      return null;
    }
  }, [net]);

  useEffect(() => { void recarregar(); }, [recarregar]);

  const ativa = net ? !!hub?.ativa : !!mockVivo?.ativa;
  useEffect(() => {
    if (!ativa) return;
    const id = window.setInterval(() => { void recarregar(); }, net ? 10_000 : 1_000);
    return () => window.clearInterval(id);
  }, [ativa, net, recarregar]);

  // Abriu ou fechou na sala: o resto do estado acompanha.
  const hubAtiva = !!hub?.ativa;
  useEffect(() => { if (net) void recarregar(); }, [hubAtiva, net, recarregar]);

  const vivo = net ? (hub?.ativa ? hub : null) : mockVivo;
  return { vivo, estado, recarregar };
}

function vivoDoMock(e: ColetaEstado): ColetaAoVivo | null {
  const o = e.ordem;
  if (!o) return null;
  const inicio = Date.parse(o.startedAt);
  const passo = e.regras.tentativaSeg * 1000;
  const proxima = inicio + (o.tentativas + 1) * passo;
  return {
    ativa: true,
    fase: 2,
    arvore: 12,
    skill: e.skill,
    skillInicio: o.skillInicio,
    tentativas: o.tentativas,
    acertos: o.acertos,
    toras: o.toras,
    terminaEm: Date.parse(o.terminaEm),
    bonus: e.bonus,
    ferramenta: e.machadinha ?? (o.recurso === 'minerio' ? 'pickaxe' : 'hatchet'),
    parado: o.parado ?? '',
    proximaEm: proxima,
    tentativaMs: passo,
    ultimo: o.tentativas > 0 ? 1 : 0,
    ultimaTora: o.tentativas > 0 ? (o.recurso === 'minerio' ? 'iron-ingot' : 'log') : '',
    ultimaQtd: o.tentativas > 0 ? e.regras.porAcerto : 0,
    origem: o.origem,
    recurso: o.recurso,
    recebidoEm: Date.now()
  };
}
